import { fetchPlayers } from '@api-service/players';
import type { Player } from '@api-service/players';
import type {
  SanityLineupAvailabilityEntry,
  SanityPredictedLineupTeamSection
} from '@lib/sanity';

/**
 * A club's ruled out, doubts and suspensions come from the FPL flags on the
 * league API, read whenever the page builds, so they follow FPL without an
 * editor touching the Studio. The section's own lists are overrides: a player
 * the editor lists sits where the editor put them, with their wording, and a
 * player on the section's hidden list is dropped. Mirrors the Studio's preview
 * in sanity/lib/availability.ts; a web test keeps the two in step.
 */

export type AvailabilityListName = 'ruledOut' | 'doubts' | 'suspensions';

export const AVAILABILITY_LISTS: AvailabilityListName[] = [
  'ruledOut',
  'doubts',
  'suspensions'
];

type AvailabilityStatus = Player['status'];

/**
 * Which list each FPL status fills. `a` (available) is fit, and `n` (not in
 * the squad) is mostly unregistered players, so neither is listed.
 */
export const AVAILABILITY_LIST_BY_STATUS: Partial<
  Record<AvailabilityStatus, AvailabilityListName>
> = {
  i: 'ruledOut',
  u: 'ruledOut',
  d: 'doubts',
  s: 'suspensions'
};

export type FlaggedPlayer = {
  optaUuid: string;
  displayName: string;
  teamShortName: string;
  imageUrl: string;
  status: AvailabilityStatus;
  chanceOfPlaying: number | null;
  news: string | null;
};

export type AvailabilitySource = 'fpl' | 'editor';

/** A list entry as the page renders it, whichever side it came from. */
export type AvailabilityEntry = SanityLineupAvailabilityEntry & {
  source: AvailabilitySource;
  imageUrl?: string;
};

export type ClubAvailability = Record<
  AvailabilityListName,
  AvailabilityEntry[]
>;

const PAGE_LIMIT = 100;
/** A whole league is ~800 players; the cap bounds a runaway cursor. */
const MAX_PAGES = 30;
const FULL_CHANCE_OF_PLAYING = 100;

/** FPL's news line, or the chance of playing when a doubt has no news. */
export const toAvailabilityDetail = (
  player: Pick<FlaggedPlayer, 'news' | 'chanceOfPlaying'>
): string | undefined => {
  const news = player.news?.trim();
  if (news) return news;
  if (
    player.chanceOfPlaying !== null &&
    player.chanceOfPlaying < FULL_CHANCE_OF_PLAYING
  ) {
    return `${player.chanceOfPlaying}% chance of playing`;
  }
  return undefined;
};

/**
 * Every player FPL lists as injured, doubtful, suspended or unavailable,
 * paged through the token-free league list. Optionally narrowed to the
 * clubs a caller may show, so a static page never carries a gated club's.
 */
export const fetchFlaggedPlayers = async (
  teamShortNames?: ReadonlySet<string>
): Promise<FlaggedPlayer[]> => {
  const flagged: FlaggedPlayer[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const response = await fetchPlayers({
      limit: PAGE_LIMIT,
      exclude: 'predictions',
      ...(cursor ? { cursor } : {})
    });
    for (const player of response.data) {
      if (!AVAILABILITY_LIST_BY_STATUS[player.status]) continue;
      if (teamShortNames && !teamShortNames.has(player.team.shortName)) {
        continue;
      }
      flagged.push({
        optaUuid: player.externalIds.optaUuid,
        displayName: player.displayName,
        teamShortName: player.team.shortName,
        imageUrl: player.imageUrl,
        status: player.status,
        chanceOfPlaying: player.chanceOfPlaying,
        news: player.news
      });
    }
    if (!response.meta?.hasMore || !response.meta.nextCursor) break;
    cursor = response.meta.nextCursor;
  }
  return flagged;
};

const byName = (a: AvailabilityEntry, b: AvailabilityEntry) =>
  (a.player.displayName ?? '').localeCompare(b.player.displayName ?? '');

/**
 * The lists a club shows. Editor entries lead each list in the editor's
 * order, then the FPL-flagged players they did not touch, A to Z. An editor
 * entry with no wording borrows FPL's, so moving a player between lists does
 * not lose the reason.
 */
export const buildClubAvailability = (
  section: Pick<
    SanityPredictedLineupTeamSection,
    'teamShortName' | 'ruledOut' | 'doubts' | 'suspensions' | 'hiddenPlayers'
  >,
  flaggedPlayers: FlaggedPlayer[]
): ClubAvailability => {
  const flaggedById = new Map(
    flaggedPlayers.map((player) => [player.optaUuid, player])
  );
  const overridden = new Set<string>([
    ...(section.hiddenPlayers ?? []).map((player) => player.optaUuid),
    ...AVAILABILITY_LISTS.flatMap((list) =>
      (section[list] ?? []).map((entry) => entry.player.optaUuid)
    )
  ]);

  const availability: ClubAvailability = {
    ruledOut: [],
    doubts: [],
    suspensions: []
  };
  for (const list of AVAILABILITY_LISTS) {
    for (const entry of section[list] ?? []) {
      const flagged = flaggedById.get(entry.player.optaUuid);
      const detail =
        entry.detail?.trim() ||
        (flagged ? toAvailabilityDetail(flagged) : undefined);
      availability[list].push({
        ...entry,
        ...(detail ? { detail } : {}),
        source: 'editor',
        imageUrl: flagged?.imageUrl
      });
    }
  }

  const fromFpl: ClubAvailability = {
    ruledOut: [],
    doubts: [],
    suspensions: []
  };
  for (const player of flaggedPlayers) {
    if (player.teamShortName !== section.teamShortName) continue;
    if (overridden.has(player.optaUuid)) continue;
    const list = AVAILABILITY_LIST_BY_STATUS[player.status];
    if (!list) continue;
    const detail = toAvailabilityDetail(player);
    fromFpl[list].push({
      _key: `fpl-${player.optaUuid}`,
      player: {
        optaUuid: player.optaUuid,
        displayName: player.displayName,
        teamShortName: player.teamShortName
      },
      ...(detail ? { detail } : {}),
      source: 'fpl',
      imageUrl: player.imageUrl
    });
  }
  for (const list of AVAILABILITY_LISTS) {
    availability[list].push(...fromFpl[list].sort(byName));
  }
  return availability;
};

/** Players the editor put back in the side: FPL's flag must not ring them on the pitch. */
export const hiddenOptaIds = (
  section: Pick<SanityPredictedLineupTeamSection, 'hiddenPlayers'> | undefined
): ReadonlySet<string> =>
  new Set((section?.hiddenPlayers ?? []).map((player) => player.optaUuid));

export const hasAnyAvailability = (availability: ClubAvailability): boolean =>
  AVAILABILITY_LISTS.some((list) => availability[list].length > 0);
